import { useFormikContext } from 'formik';
import React from 'react';

import { Messages } from '../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../commonInfra/components/FormikCheckbox';
import { isUserVisibilityLimited } from '../../../../sites/SitesFunctions';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { SettingsStyles } from '../../../styles/CommonStyles';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../site-settings/ReactSiteSettingsForSiteAdmin';

export type SubscriptionsSectionProps = SiteSettingsForSiteAdminProps<'subscriptionSettings' | 'userVisibility'>;

// Formik values
export const SUBSCRIPTIONS_ENABLED = 'settings.subscriptionSettings.subscriptionsEnabled';
export const SUBSCRIBE_OTHERS_ENABLED = 'settings.subscriptionSettings.subscribeOthersEnabled';
export const ALLOW_SUBSCRIPTION_ATTACHMENTS = 'settings.subscriptionSettings.allowSubscriptionAttachments';
export const CUSTOM_SCHEDULE_SUBSCRIPTIONS = 'settings.subscriptionSettings.customScheduleSubscriptionForSiteEnabled';

export function SubscriptionsSection(props: SubscriptionsSectionProps) {
  const formik = useFormikContext<SubscriptionsSectionProps>();

  // The backend sends the subscriptionSettings object as part of the site settings if
  // subscriptions are enabled at the server level. (TFS: 616257)
  if (!formik.initialValues.settings.subscriptionSettings) {
    return null;
  }

  return (
    <SettingsSection title={Messages.subscriptions()} description={Messages.subscriptionsDetails()} testId={'subscriptions-setting'}>
      <FormikCheckbox name={SUBSCRIPTIONS_ENABLED}
        label={Messages.allowSubscriptions()}
        testId={'allow-subscriptions'}
      >
        <div className={SettingsStyles.checkboxGroup}>
          <div>
            <FormikCheckbox name={SUBSCRIBE_OTHERS_ENABLED}
              label={Messages.allowSubscribeOthers()}
              testId={'allow-subscribe-others'}
              childrenSkipIndent={true}
              alwaysShowChildren={true}
            />

            {isUserVisibilityLimited(formik.initialValues.settings.userVisibility) &&
              <div className={SettingsStyles.checkboxMessageIndent} data-tb-test-id={'subscription-uservisibility-warning'}>
                {Messages.userVisibilitySubscribeOthersWarning()}
              </div>}
          </div>

          {formik.initialValues.settings.subscriptionSettings.serverAllowSubscriptionAttachments &&
            <FormikCheckbox name={ALLOW_SUBSCRIPTION_ATTACHMENTS}
            label={Messages.allowSubscriptionAttachments()}
            testId={'allow-subscription-attachments'} />
          }

          {isSelfServiceSchedulesEnabled() &&
            // Once this setting is enabled, it is enabled permanently and cannot be turned off
            <FormikCheckbox name={CUSTOM_SCHEDULE_SUBSCRIPTIONS}
            label={Messages.subscriptionCustomScheduleForSite()}
            testId={'enable-custom-schedule-subcription-for-site'}
            disabled={formik.initialValues.settings.subscriptionSettings.customScheduleSubscriptionForSiteEnabled}
            childrenSkipIndent={true}
            alwaysShowChildren={true}
            >
              <div className={SettingsStyles.checkboxMessageIndent}>
                {Messages.subscriptionOnCustomScheduleCannotTurnBack()}
              </div>
            </FormikCheckbox>
          }
        </div>
      </FormikCheckbox>
    </SettingsSection>
  );
}

/**
 * This attribute was being sent using FeatureFlags before development Feature flags were
 * a pattern. This attribute should be moved out of FeatureFlags and into a proper server/site
 * level property.
 */
function isSelfServiceSchedulesEnabled() {
  return FeatureFlags.selfServiceSchedulesEnabled;
}

export const SubscriptionsSpec = componentRegistryEntry({
  name: 'SubscriptionsSection',
  component: SubscriptionsSection
});
